import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatDate, formatDateTime, computeBalance, type CreditCustomer, type CreditTransaction } from '@/lib/credit';
import { downloadCSV } from '@/lib/csv';

interface Props {
  customers: CreditCustomer[];
  transactions: CreditTransaction[];
  currency: string;
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '');
}

export default function ReportsView({ customers, transactions, currency }: Props) {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const inRange = useMemo(() => {
    const fromTs = from ? new Date(from).getTime() : -Infinity;
    const toTs = to ? new Date(to).getTime() + 86400000 : Infinity;
    return transactions.filter((tx) => {
      const t = new Date(tx.created_at).getTime();
      return t >= fromTs && t <= toTs;
    });
  }, [transactions, from, to]);

  const totals = useMemo(() => {
    let credits = 0;
    let payments = 0;
    let adjustments = 0;
    inRange.forEach((tx) => {
      if (tx.type === 'credit') credits += Number(tx.amount) || 0;
      if (tx.type === 'payment') payments += Number(tx.amount) || 0;
      if (tx.type === 'adjustment') adjustments += Number(tx.amount) || 0;
    });
    return { credits, payments, adjustments };
  }, [inRange]);

  const outstanding = useMemo(
    () => customers.reduce((sum, c) => sum + Math.max(0, computeBalance(transactions.filter((t) => t.customer_id === c.id))), 0),
    [customers, transactions]
  );

  const monthly = useMemo(() => {
    const months: { key: string; label: string; credits: number; payments: number }[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: monthKey(d), label: monthLabel(d), credits: 0, payments: 0 });
    }
    const index = new Map(months.map((m) => [m.key, m]));
    transactions.forEach((tx) => {
      const m = index.get(monthKey(new Date(tx.created_at)));
      if (!m) return;
      if (tx.type === 'credit') m.credits += Number(tx.amount) || 0;
      if (tx.type === 'payment') m.payments += Number(tx.amount) || 0;
    });
    return months;
  }, [transactions]);

  const maxMonthly = Math.max(1, ...monthly.map((m) => Math.max(m.credits, m.payments)));

  const balances = useMemo(() => {
    return customers
      .map((c) => ({ customer: c, balance: computeBalance(transactions.filter((t) => t.customer_id === c.id)) }))
      .filter((r) => r.balance > 0)
      .sort((a, b) => b.balance - a.balance);
  }, [customers, transactions]);

  const payments = useMemo(
    () => inRange.filter((tx) => tx.type === 'payment').slice(0, 40),
    [inRange]
  );

  const customerName = (id: number) => customers.find((c) => c.id === id)?.full_name || t('crf.rep_deleted_client', { defaultValue: 'Client supprimé' });

  const exportBalancesCSV = () => {
    downloadCSV(
      'soldes-clients.csv',
      ['Client', 'Téléphone', 'Solde', 'Devise'],
      balances.map(({ customer: c, balance: b }) => [
        c.full_name,
        c.phone || '',
        String(b),
        currency,
      ])
    );
  };

  const exportHistoryCSV = () => {
    downloadCSV(
      'historique-credit.csv',
      ['Date', 'Client', 'Type', 'Montant', 'Description', 'Mode paiement', 'Devise'],
      inRange.map((tx) => [
        formatDateTime(tx.created_at),
        customerName(tx.customer_id),
        tx.type === 'credit' ? 'Crédit' : tx.type === 'payment' ? 'Paiement' : 'Ajustement',
        String(tx.amount),
        tx.description || '',
        tx.payment_method || '',
        currency,
      ])
    );
  };

  return (
    <div className="space-y-5">
      {/* Export buttons */}
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <button onClick={exportBalancesCSV}
          className="flex items-center gap-1.5 px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-download-2-line"></i>{t('crf.rep_export_balances', { defaultValue: 'Soldes CSV' })}
        </button>
        <button onClick={exportHistoryCSV}
          className="flex items-center gap-1.5 px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-file-excel-2-line"></i>{t('crf.rep_export_history', { defaultValue: 'Historique CSV' })}
        </button>
      </div>

      {/* Date filters */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-foreground-600 mb-1">{t('crf.rep_from', { defaultValue: 'Du' })}</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-semibold text-foreground-600 mb-1">{t('crf.rep_to', { defaultValue: 'Au' })}</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
        </div>
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); }}
            className="h-10 px-4 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
            {t('crf.rep_reset', { defaultValue: 'Réinitialiser' })}
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-xs text-foreground-500 mb-1">{t('crf.stat_outstanding', { defaultValue: 'Crédit total en cours' })}</div>
          <div className="text-lg font-bold font-heading text-primary-700">{formatMoney(outstanding, currency)}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-xs text-foreground-500 mb-1">{t('crf.rep_credits_period', { defaultValue: 'Crédits (période)' })}</div>
          <div className="text-lg font-bold font-heading text-foreground-950">{formatMoney(totals.credits, currency)}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-xs text-foreground-500 mb-1">{t('crf.rep_payments_period', { defaultValue: 'Paiements (période)' })}</div>
          <div className="text-lg font-bold font-heading text-accent-600">{formatMoney(totals.payments, currency)}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-xs text-foreground-500 mb-1">{t('crf.rep_adjust_period', { defaultValue: 'Ajustements (période)' })}</div>
          <div className="text-lg font-bold font-heading text-secondary-600">{formatMoney(totals.adjustments, currency)}</div>
        </div>
      </div>

      {/* Monthly evolution */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{t('crf.rep_credits_vs_payments', { defaultValue: 'Crédits vs paiements' })}</h3>
        <p className="text-xs text-foreground-400 mb-4">{t('crf.rep_last_6', { defaultValue: '6 derniers mois' })}</p>
        <div className="flex items-end justify-between gap-2 h-40">
          {monthly.map((m) => (
            <div key={m.key} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
              <div className="w-full flex items-end justify-center gap-1 h-32">
                <div className="w-2.5 md:w-3 rounded-t bg-primary-400" style={{ height: `${(m.credits / maxMonthly) * 100}%` }} title={`Crédits ${formatMoney(m.credits, currency)}`} />
                <div className="w-2.5 md:w-3 rounded-t bg-accent-400" style={{ height: `${(m.payments / maxMonthly) * 100}%` }} title={`Paiements ${formatMoney(m.payments, currency)}`} />
              </div>
              <span className="text-[10px] text-foreground-400 truncate">{m.label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary-400"></span>{t('crf.rep_credits', { defaultValue: 'Crédits' })}</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-accent-400"></span>{t('crf.rep_payments', { defaultValue: 'Paiements' })}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Customer balances */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">{t('crf.rep_client_balances', { defaultValue: 'Soldes clients' })}</h3>
          {balances.length === 0 ? (
            <p className="text-sm text-foreground-400 py-6 text-center">{t('crf.rep_no_balance', { defaultValue: 'Aucun solde en cours' })}</p>
          ) : (
            <div className="space-y-2.5">
              {balances.slice(0, 12).map(({ customer, balance }) => (
                <div key={customer.id} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-foreground-700 truncate">{customer.full_name}</span>
                  <span className="text-sm font-semibold text-primary-700 flex-shrink-0">{formatMoney(balance, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment history */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">{t('crf.rep_payment_history', { defaultValue: 'Historique des paiements' })}</h3>
          {payments.length === 0 ? (
            <p className="text-sm text-foreground-400 py-6 text-center">{t('crf.rep_no_payment', { defaultValue: 'Aucun paiement sur la période' })}</p>
          ) : (
            <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
              {payments.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-foreground-800 truncate">{customerName(tx.customer_id)}</div>
                    <div className="text-[11px] text-foreground-400">{formatDate(tx.created_at)}{tx.payment_method ? ` · ${tx.payment_method}` : ''}</div>
                  </div>
                  <span className="font-semibold text-accent-600 flex-shrink-0">{formatMoney(Number(tx.amount), currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}